import type { MedicareOptOutRecord } from "@api/services/psv/sources/medicare-opt-out/contracts";
import { medicareAffidavitIssueKey } from "@api/shared-runtime/psv-review";
import { formatDate } from "../../../lib/format-date";
import { CandidateIssueLabel } from "../../provider-screenings/issues/candidate-issue-label";
import type { ReportIssue } from "../../provider-screenings/types";

function isOptOutRecord(value: unknown): value is MedicareOptOutRecord {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.firstName === "string" &&
    typeof record.lastName === "string" &&
    typeof record.effectiveDate === "string" &&
    typeof record.endDate === "string"
  );
}

/** The opt-out records a Medicare result reports, in the order the API
 * numbers them. */
function optOutRecords(facts: Record<string, unknown> | undefined) {
  const records = facts?.records;
  return Array.isArray(records) ? records.filter(isOptOutRecord) : [];
}

function RecordField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words">{value}</dd>
    </div>
  );
}

/**
 * Each opt-out record CMS reported for the provider's NPI, shown like a
 * screening match: who and where it is, its opt-out period, and how it
 * compares with an earlier run. An NPI can have several records; each one's
 * label is matched by its position, which is how the API keys it.
 */
export function MedicareOptOutRecords({
  facts,
  issues = [],
}: {
  facts: Record<string, unknown> | undefined;
  issues?: readonly ReportIssue[];
}) {
  const records = optOutRecords(facts);
  if (!records.length) {
    return null;
  }
  return (
    <section aria-label="Opt-out records" className="space-y-3 text-xs">
      {records.map((record, index) => {
        const issueKey = medicareAffidavitIssueKey(index + 1);
        const issue = issues.find(
          ({ candidateKey }) => candidateKey === issueKey,
        );
        return (
          <div
            key={`${record.effectiveDate}-${index}`}
            className="space-y-2 border-t pt-3 first:border-t-0 first:pt-0"
          >
            <p className="font-medium">
              {record.firstName} {record.lastName}
            </p>
            {issue && <CandidateIssueLabel issue={issue} />}
            <dl className="grid gap-x-3 gap-y-2 sm:grid-cols-2">
              <RecordField
                label="Opt-out period"
                value={`${formatDate(record.effectiveDate)} – ${formatDate(record.endDate)}`}
              />
              <RecordField
                label="Specialty"
                value={record.specialty || "Not supplied"}
              />
              <RecordField
                label="Address"
                value={record.address || "Not supplied"}
              />
              <RecordField
                label="Eligible to order and refer"
                value={record.eligibleToOrderAndRefer ? "Yes" : "No"}
              />
              <RecordField
                label="Last updated by CMS"
                value={formatDate(record.lastUpdated)}
              />
            </dl>
          </div>
        );
      })}
    </section>
  );
}
